import { BY_SYMBOL } from './elements';

// Minimal chemical-formula parser for the viz widgets. Handles element symbols
// with optional counts and one level of parentheses with a multiplier, e.g.
// "H2O", "CO2", "C6H12O6", "Ca(OH)2", "Mg(NO3)2". Resilient: unknown tokens are
// skipped, malformed input yields an empty result rather than throwing.

export interface AtomCount {
  symbol: string;
  count: number;
}

// Returns ordered atom counts (in first-seen order), e.g. H2O → [H:2, O:1].
export function parseFormula(formula: string): AtomCount[] {
  if (!formula || typeof formula !== 'string') return [];
  const order: string[] = [];
  const counts: Record<string, number> = {};

  const add = (symbol: string, n: number) => {
    if (!(symbol in counts)) {
      counts[symbol] = 0;
      order.push(symbol);
    }
    counts[symbol] += n;
  };

  // Recursive-descent over tokens: Symbol | "(" group ")" multiplier | digits.
  let i = 0;
  const s = formula.trim();

  function parseGroup(): Record<string, number> {
    const local: Record<string, number> = {};
    const localOrder: string[] = [];
    const bump = (sym: string, n: number) => {
      if (!(sym in local)) {
        local[sym] = 0;
        localOrder.push(sym);
      }
      local[sym] += n;
    };

    while (i < s.length) {
      const ch = s[i];
      if (ch === '(') {
        i++; // consume '('
        const inner = parseGroup();
        if (s[i] === ')') i++; // consume ')'
        const mult = readNumber();
        for (const [sym, n] of Object.entries(inner)) bump(sym, n * mult);
      } else if (ch === ')') {
        break; // let caller consume ')'
      } else if (/[A-Z]/.test(ch)) {
        // Element symbol: uppercase + optional lowercase.
        let sym = ch;
        i++;
        if (i < s.length && /[a-z]/.test(s[i])) {
          sym += s[i];
          i++;
        }
        const n = readNumber();
        bump(sym, n);
      } else {
        // Unexpected char (whitespace, dot hydrate, etc.) — skip it.
        i++;
      }
    }
    // Preserve first-seen order via the returned record's insertion order.
    const ordered: Record<string, number> = {};
    for (const sym of localOrder) ordered[sym] = local[sym];
    return ordered;
  }

  function readNumber(): number {
    let num = '';
    while (i < s.length && /[0-9]/.test(s[i])) {
      num += s[i];
      i++;
    }
    return num ? parseInt(num, 10) : 1;
  }

  const top = parseGroup();
  for (const [sym, n] of Object.entries(top)) add(sym, n);

  return order.map((symbol) => ({ symbol, count: counts[symbol] }));
}

// Total molar mass of a parsed/raw formula (0 if nothing recognised).
export function molarMass(formula: string): number {
  return parseFormula(formula).reduce((sum, { symbol, count }) => {
    const el = BY_SYMBOL[symbol];
    return el ? sum + el.mass * count : sum;
  }, 0);
}

// Mass-percent composition by element. Returns [] if mass can't be computed.
export interface MassPercent {
  symbol: string;
  count: number;
  mass: number; // total contributed mass
  percent: number; // 0-100
}

export function percentComposition(formula: string): MassPercent[] {
  const atoms = parseFormula(formula);
  const total = molarMass(formula);
  if (total <= 0) return [];
  return atoms
    .map(({ symbol, count }) => {
      const el = BY_SYMBOL[symbol];
      const mass = el ? el.mass * count : 0;
      return { symbol, count, mass, percent: (mass / total) * 100 };
    })
    .filter((p) => p.mass > 0);
}
